"use client";
/**
 * The full nutrition panel for one serving, with the basis its micronutrients
 * were measured in stated on the panel itself.
 *
 * This is where the two endpoints' disagreement is visible rather than hidden.
 * `food.get.v4` reports vitamin A, vitamin C, calcium and iron as METRIC
 * AMOUNTS — micrograms for vitamin A, milligrams for the other three —
 * and `recipe.get.v2` reports the same four keys as PERCENTAGES OF DAILY
 * INTAKE. A `28` and a `2` in the same slot mean unrelated things, so the panel
 * prints each figure with the unit its own endpoint measured it in and says in
 * words which frame it is in.
 *
 * Every figure that arrived is shown and every figure that did not is absent —
 * not zeroed. `transFat` and `addedSugars` are null on all ten captured food
 * servings, and rendering them as `0 g` would be this family asserting a
 * measurement the vendor never made.
 */
import type { ReactElement } from "react";
import {
  CresultcardFactGroup,
  CresultcardNotice,
  CresultcardSectionHeader,
  CresultcardStack,
  CresultcardFactGroupShape,
  CresultcardFactWeight,
  CresultcardStackDensity,
  CresultcardTone,
  type CresultcardFactEntry,
} from "@/components/ui/Cresultcard";
import {
  CNUTRITIONCARD_ADDED_SUGARS_LABEL,
  CNUTRITIONCARD_CARBOHYDRATE_LABEL,
  CNUTRITIONCARD_CHOLESTEROL_LABEL,
  CNUTRITIONCARD_ENERGY_LABEL,
  CNUTRITIONCARD_ENERGY_UNIT,
  CNUTRITIONCARD_FAT_LABEL,
  CNUTRITIONCARD_FIBER_LABEL,
  CNUTRITIONCARD_MACROS_TITLE,
  CNUTRITIONCARD_MICRONUTRIENTS_TITLE,
  CNUTRITIONCARD_MONO_FAT_LABEL,
  CNUTRITIONCARD_POLY_FAT_LABEL,
  CNUTRITIONCARD_POTASSIUM_LABEL,
  CNUTRITIONCARD_PROTEIN_LABEL,
  CNUTRITIONCARD_SATURATED_FAT_LABEL,
  CNUTRITIONCARD_SODIUM_LABEL,
  CNUTRITIONCARD_SUGAR_LABEL,
  CNUTRITIONCARD_TRANS_FAT_LABEL,
  CNUTRITION_UNIT_GRAMS,
  CNUTRITION_UNIT_MILLIGRAMS,
  C_NUTRITION_MICRO_BASIS_NOTE,
} from "./cnutritioncard.constants";
import { cnutritioncardMicros } from "./cnutritioncard.model";
import type {
  CnutritioncardNutrients,
  CnutritioncardServing,
} from "./cnutritioncard.types";

const VALUE_SEPARATOR = " ";

/** One measured figure with its unit, or nothing at all where it was absent. */
function measured({
  label,
  value,
  unit,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly unit: string;
}): readonly CresultcardFactEntry[] {
  if (value == null || !Number.isFinite(value)) {
    return [];
  }
  return [
    {
      label,
      value: `${String(value)}${VALUE_SEPARATOR}${unit}`,
      weight: CresultcardFactWeight.Short,
    },
  ];
}

/** The whole macro roster in the vendor's own order, absences omitted. */
function macroEntries(
  nutrients: CnutritioncardNutrients,
): readonly CresultcardFactEntry[] {
  return [
    ...measured({
      label: CNUTRITIONCARD_ENERGY_LABEL,
      value: nutrients.calories,
      unit: CNUTRITIONCARD_ENERGY_UNIT,
    }),
    ...measured({
      label: CNUTRITIONCARD_CARBOHYDRATE_LABEL,
      value: nutrients.carbohydrate,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_PROTEIN_LABEL,
      value: nutrients.protein,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_FAT_LABEL,
      value: nutrients.fat,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_SATURATED_FAT_LABEL,
      value: nutrients.saturatedFat,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_TRANS_FAT_LABEL,
      value: nutrients.transFat,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_POLY_FAT_LABEL,
      value: nutrients.polyunsaturatedFat,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_MONO_FAT_LABEL,
      value: nutrients.monounsaturatedFat,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_FIBER_LABEL,
      value: nutrients.fiber,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_SUGAR_LABEL,
      value: nutrients.sugar,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_ADDED_SUGARS_LABEL,
      value: nutrients.addedSugars,
      unit: CNUTRITION_UNIT_GRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_CHOLESTEROL_LABEL,
      value: nutrients.cholesterol,
      unit: CNUTRITION_UNIT_MILLIGRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_SODIUM_LABEL,
      value: nutrients.sodium,
      unit: CNUTRITION_UNIT_MILLIGRAMS,
    }),
    ...measured({
      label: CNUTRITIONCARD_POTASSIUM_LABEL,
      value: nutrients.potassium,
      unit: CNUTRITION_UNIT_MILLIGRAMS,
    }),
  ];
}

interface CnutritioncardNutrientPanelProps {
  readonly serving: CnutritioncardServing;
}

export function CnutritioncardNutrientPanel({
  serving,
}: CnutritioncardNutrientPanelProps): ReactElement {
  const macros = macroEntries(serving.nutrients);
  const micros = cnutritioncardMicros(serving.micronutrients);

  return (
    <CresultcardStack
      density={CresultcardStackDensity.Regular}
    >
      <CresultcardSectionHeader
        title={CNUTRITIONCARD_MACROS_TITLE}
      />
      <CresultcardFactGroup
        entries={macros}
        group={CresultcardFactGroupShape.Column}
      />
      {micros.length > 0 && serving.micronutrients != null && (
        <CresultcardStack
          density={CresultcardStackDensity.Snug}
        >
          <CresultcardSectionHeader
            title={CNUTRITIONCARD_MICRONUTRIENTS_TITLE}
          />
          <CresultcardNotice
            tone={CresultcardTone.Muted}
          >
            {C_NUTRITION_MICRO_BASIS_NOTE[serving.micronutrients.basis]}
          </CresultcardNotice>
          <CresultcardFactGroup
            entries={micros.map((micro) => ({
              label: micro.label,
              value: `${String(micro.value)}${VALUE_SEPARATOR}${micro.unit}`,
              weight: CresultcardFactWeight.Short,
            }))}
            group={CresultcardFactGroupShape.Column}
          />
        </CresultcardStack>
      )}
    </CresultcardStack>
  );
}
